
import {
  FolderIcon,
  FolderKey,
  FolderLock,
  MoreHorizontalIcon,
  Plus,
  ChevronRight,
  Trash2,
  FolderPlus,
  FolderPen,
  FolderInput,
  LockKeyhole,
} from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router";
import { toast } from "sonner";

import { useDeleteFolderMutation, useFolderMoveDropTarget, useItemMoveExpandTarget } from "@/modules/mine";
import { CreateFolder } from "@/modules/mine/components/folder/create-folder";
import { EditFolder } from "@/modules/mine/components/folder/edit-folder";
import { MoveFolder } from "@/modules/mine/components/folder/move-folder";
import { usePrivacySession } from "@/modules/mine/privacy/use-privacy-session";
import { useDialog } from "@/shared/components/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shared/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import {
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  useSidebar,
} from "@/shared/ui/sidebar";
import { useFolderPrivacyDialog } from "@/widgets/privacy/use-folder-privacy-dialog";

import type { Folder, RouteInfo } from "./types";



/**
 * 组件 Props 接口
 */
interface SidebarFoldersProps {
  items: Folder[];
  routeInfo: RouteInfo;
}

/**
 * 单个文件夹项组件（支持子文件夹）
 */
interface FolderItemProps {
  folder: Folder;
  routeInfo: RouteInfo;
  isMobile: boolean;
  expandedIds: Set<string>;
  onToggleExpand: (id: string) => void;
  onExpand: (id: string) => void;
  onDeleteFolder: (folder: Folder) => void;
  onEditFolder: (folder: Folder) => void;
  onCreateFolder: (id?: string) => void;
  isTopLevel: boolean; // 是否为顶级文件夹
  shouldShowPlaceholder: boolean; // 是否应该显示占位按钮
  onMoveFolder: (folder: Folder) => void;
}

interface FolderActionsMenuProps {
  folder: Folder;
  isMobile: boolean;
  onCreateFolder: (id?: string) => void;
  onMoveFolder: (folder: Folder) => void;
  onEditFolder: (folder: Folder) => void;
  onDeleteFolder: (folder: Folder) => void;
}

function FolderActionsMenu({
  folder,
  isMobile,
  onCreateFolder,
  onMoveFolder,
  onEditFolder,
  onDeleteFolder,
}: FolderActionsMenuProps) {
  const { isUnlocked } = usePrivacySession();
  const openPrivacyDialog = useFolderPrivacyDialog();
  const isLocked = Boolean(folder.isPrivate && !isUnlocked);
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <SidebarMenuAction
          showOnHover
          className="rounded-sm data-[state=open]:bg-accent"
        >
          <MoreHorizontalIcon />
          <span className="sr-only">More</span>
        </SidebarMenuAction>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="min-w-32 rounded-lg"
        side={isMobile ? "bottom" : "right"}
        align={isMobile ? "end" : "start"}
      >
        {isLocked ? <DropdownMenuItem onClick={() => void openPrivacyDialog("unlock", folder)}>
          <LockKeyhole />
          <span>解锁查看</span>
        </DropdownMenuItem> : <>
        <DropdownMenuItem onClick={() => onCreateFolder(folder.id)}>
          <FolderPlus />
          <span>添加子文件夹</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onEditFolder(folder)}>
          <FolderPen />
          <span>编辑</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onMoveFolder(folder)}>
          <FolderInput />
          <span>移动</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => void openPrivacyDialog(folder.isEncrypted ? "unprotect" : "protect", folder)}>
          <LockKeyhole />
          <span>{folder.isEncrypted ? "取消私密保护" : "设为私密"}</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => onDeleteFolder(folder)}
          variant="destructive"
        >
          <Trash2 />
          <span>删除</span>
        </DropdownMenuItem>
        </>}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function FolderItem({ folder, routeInfo, isMobile, expandedIds, onToggleExpand, onExpand, onDeleteFolder, onEditFolder, onCreateFolder, isTopLevel, shouldShowPlaceholder, onMoveFolder }: FolderItemProps) {
  const { isUnlocked } = usePrivacySession();
  const isLocked = Boolean(folder.isPrivate && !isUnlocked);
  const FolderGlyph = folder.isPrivate ? (isUnlocked ? FolderKey : FolderLock) : FolderIcon;
  const hasChildren = (folder.children?.length ?? 0) > 0;
  const isExpanded = expandedIds.has(folder.id);
  const { ref: dropTargetRef, isDropTarget } = useFolderMoveDropTarget({
    folderId: folder.id,
    folderName: folder.name,
    disabled: isMobile || isLocked,
  });

  useEffect(() => {
    if (!hasChildren || isExpanded || !isDropTarget) {
      return;
    }

    const timer = window.setTimeout(() => onExpand(folder.id), 500);
    return () => window.clearTimeout(timer);
  }, [folder.id, hasChildren, isDropTarget, isExpanded, onExpand]);

  // 没有子文件夹，渲染普通菜单项
  if (!hasChildren) {
    // 顶级文件夹：如果所有顶级文件夹都没有子文件夹，则不显示占位按钮
    // 子文件夹：始终显示占位按钮以保持对齐
    const showPlaceholder = !isTopLevel || shouldShowPlaceholder;
    
    return (
      <SidebarMenuItem ref={dropTargetRef}>
        <SidebarMenuButton
          asChild
          tooltip={folder.name}
          isActive={folder.id === routeInfo.folderId}
          className={isDropTarget ? "bg-primary/10 text-primary ring-2 ring-inset ring-primary/70" : undefined}
        >
          <Link to={`/mine/folders/${folder.id}`}>
            {/* 占位元素：子文件夹始终显示，顶级文件夹根据情况显示 */}
            {showPlaceholder && (
              <ChevronRight className="opacity-0 pointer-events-none" />
            )}
            <FolderGlyph className="size-4" />
            <span>{folder.name}</span>
          </Link>
        </SidebarMenuButton>
        <FolderActionsMenu
          folder={folder}
          isMobile={isMobile}
          onCreateFolder={onCreateFolder}
          onEditFolder={onEditFolder}
          onMoveFolder={onMoveFolder}
          onDeleteFolder={onDeleteFolder}
        />
      </SidebarMenuItem>
    );
  }

  // 有子文件夹，使用 Collapsible 结构（参考官方示例）
  return (
    
      <Collapsible
        open={isExpanded}
        onOpenChange={() => onToggleExpand(folder.id)}
      >
        <SidebarMenuItem ref={dropTargetRef}>
          {/* 展开/收起按钮 */}
          <CollapsibleTrigger asChild>
              <SidebarMenuButton
                className={`group/collapsible [&[data-state=open]>svg:first-child]:rotate-90 ${
                  isDropTarget ? "bg-primary/10 text-primary ring-2 ring-inset ring-primary/70" : ""
                }`}
                tooltip={folder.name}
                isActive={
                  folder.id === routeInfo.folderId
                }
              >
                <ChevronRight className="transition-transform" />
                <Link className="flex-1 flex items-center gap-2 overflow-hidden" to={`/mine/folders/${folder.id}`}>
                  <FolderGlyph className="size-4" />
                  <span className="whitespace-nowrap overflow-hidden text-ellipsis">{folder.name}</span>
                </Link>
              </SidebarMenuButton>
          </CollapsibleTrigger>
          {/* 更多操作菜单 */}
          <FolderActionsMenu
            folder={folder}
            isMobile={isMobile}
            onCreateFolder={onCreateFolder}
            onEditFolder={onEditFolder}
            onMoveFolder={onMoveFolder}
            onDeleteFolder={onDeleteFolder}
          />
        </SidebarMenuItem>
        <CollapsibleContent>
          <SidebarMenuSub className="mr-0 pr-0">
            {folder.children!.map((childFolder) => (
              <FolderItem 
                key={childFolder.id}
                folder={childFolder}
                routeInfo={routeInfo}
                isMobile={isMobile}
                expandedIds={expandedIds}
                onToggleExpand={onToggleExpand}
                onExpand={onExpand}
                onDeleteFolder={onDeleteFolder}
                onEditFolder={onEditFolder}
                onCreateFolder={onCreateFolder}
                isTopLevel={false}
                shouldShowPlaceholder={true}
                onMoveFolder={onMoveFolder}
              />
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </Collapsible>
    
  );
}

/**
 * 侧边栏文件夹列表组件
 */
function SidebarFolders({ items, routeInfo }: SidebarFoldersProps) {
  const { isMobile } = useSidebar();
  const dialog = useDialog();
  const deleteFolderMutation = useDeleteFolderMutation();
  const [isSectionOpen, setIsSectionOpen] = useState(true);
  const { ref: sectionDropRef, isDropTarget: isSectionDropTarget } = useItemMoveExpandTarget("folders", isMobile);
  const [expandedFolderIds, setExpandedFolderIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isSectionOpen || !isSectionDropTarget) {
      return;
    }

    const timer = window.setTimeout(() => setIsSectionOpen(true), 500);
    return () => window.clearTimeout(timer);
  }, [isSectionDropTarget, isSectionOpen]);

  // 递归查找文件夹路径（从子文件夹找到根文件夹）
  const findFolderPath = useCallback((folders: Folder[], targetId: string, path: string[] = []): string[] | null => {
    for (const folder of folders) {
      if (folder.id === targetId) {
        return path;
      }
      if (folder.children) {
        const foundPath = findFolderPath(folder.children, targetId, [...path, folder.id]);
        if (foundPath) return foundPath;
      }
    }
    return null;
  }, []);

  // 根据当前路由的 folderId 自动展开父级文件夹
  useEffect(() => {
    if (routeInfo.folderId) {
      const path = findFolderPath(items, routeInfo.folderId);
      if (path && path.length > 0) {
        setExpandedFolderIds((prev) => {
          const newSet = new Set(prev);
          path.forEach((id) => newSet.add(id));
          return newSet;
        });
      }
    }
  }, [routeInfo.folderId, items, findFolderPath]);

  // 切换文件夹展开/收起状态
  const handleToggleExpand = (folderId: string) => {
    setExpandedFolderIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
      }
      return newSet;
    });
  };

  const handleExpand = useCallback((folderId: string) => {
    setExpandedFolderIds((prev) => {
      if (prev.has(folderId)) {
        return prev;
      }

      const next = new Set(prev);
      next.add(folderId);
      return next;
    });
  }, []);

  // 打开新建收藏集对话框
  const handleCreateFolder = (id?: string) => {
    void dialog.open(CreateFolder, { parentId: id }, {
      title: "新建文件夹",
      description: "创建一个新的文件夹来组织你的内容。",
    });
  };

  const handleDeleteFolder = useCallback(async (folder: Folder) => {
    await dialog.confirmDelete({
      entityLabel: "文件夹",
      entityName: folder.name,
      consequence: "该文件夹下的内容也会一并删除，且无法撤销。",
      onConfirm: async () => {
        await deleteFolderMutation.mutateAsync(folder.id);
        toast.success("文件夹删除成功");
      },
    });
  }, [deleteFolderMutation, dialog]);

  const handleEditFolder = useCallback((folder: Folder) => {
    void dialog.open(EditFolder, { folder }, {
      title: "编辑文件夹",
      description: "修改文件夹名称和描述。",
    });
  }, [dialog]);

  const handleMoveFolder = (folder: Folder) => {
    void dialog.open(MoveFolder, { folder, folders: items }, {
      title: "移动文件夹",
      description: "选择 " + folder.name + " 的新父级目录。",
      placement: "top",
      className: "w-full !max-w-lg",
    });
  };

  // 检查是否至少有一个顶级文件夹有子文件夹
  // 如果有，则所有顶级文件夹都需要占位按钮以保持对齐
  // 如果都没有，则顶级文件夹不需要占位按钮，统一靠前对齐
  const hasAnyTopLevelFolderWithChildren = items.some(
    (folder) => folder.children && folder.children.length > 0
  );

  return (
    <SidebarMenu>
      <Collapsible open={isSectionOpen} onOpenChange={setIsSectionOpen}>
        <SidebarMenuItem ref={sectionDropRef} className="group flex items-center justify-between pr-1">
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="group/header flex min-w-0 flex-1 items-center gap-0.5 rounded-md text-left"
            >
              <SidebarGroupLabel className="text-[13px] font-semibold text-muted-foreground/80">文件夹</SidebarGroupLabel>
              <ChevronRight className="size-4 text-muted-foreground/60 transition-transform group-data-[state=open]/header:rotate-90" />
              <span className="sr-only">切换文件夹列表</span>
            </button>
          </CollapsibleTrigger>
          <button
            type="button"
            title="新建文件夹"
            aria-label="新建文件夹"
            className="flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground/60 transition-colors hover:bg-accent hover:text-foreground"
            onClick={() => handleCreateFolder()}
          >
            <Plus className="size-4" />
          </button>
          </SidebarMenuItem>
        <CollapsibleContent>
          <SidebarMenu className="mb-4">
            {/* 渲染文件夹列表（支持子文件夹） */}
            {items.map((folder) => (
              <FolderItem
                key={folder.id}
                folder={folder}
                routeInfo={routeInfo}
                isMobile={isMobile}
                expandedIds={expandedFolderIds}
                onToggleExpand={handleToggleExpand}
                onExpand={handleExpand}
                onDeleteFolder={handleDeleteFolder}
                onEditFolder={handleEditFolder}
                onCreateFolder={handleCreateFolder}
                isTopLevel={true}
                shouldShowPlaceholder={hasAnyTopLevelFolderWithChildren}
                onMoveFolder={handleMoveFolder}
              />
            ))}
          </SidebarMenu>
        </CollapsibleContent>
      </Collapsible>
    </SidebarMenu>
  );
}

export default SidebarFolders;
